import type { ComponentType } from 'react';

import type { GeoMarkupStore } from '@/store';
import type { Coordinates, ViewMode } from '@/types';

export type { ViewMode };

// ========== Contracts ==========

export type OverlayKind = 'detail' | 'edit' | 'new';

/**
 * Unified per-artifact slice contract. Implemented directly by every
 * artifact slice (`tasksSlice`, `notesSlice`, future `drawingsSlice`, ...).
 *
 * NO adapter pattern — each slice natively exposes this interface. The
 * generic `_T` is the domain entity type (`Task`, `Note`, ...); it is a
 * logical placeholder so future generic helpers can specialise on the
 * domain entity, while present-day consumers use `ArtifactSlice` without
 * the generic parameter. Panel components that need the typed domain
 * field (`selectedTask` / `detailedNote`) read those slice-specific
 * fields directly instead of going through this contract.
 */
export interface ArtifactSlice<_T = unknown> {
  // --- State ---
  selectedId: string | null;
  detailsIsOpen: boolean;
  formIsOpen: boolean;
  formMode: 'create' | 'edit' | null;
  formEditingId: string | null;
  formAnchorPoint: Coordinates | null;
  /**
   * Whether the form has unsaved user input. Synced from the form component
   * via `setFormIsDirty`. Read by `formConflictSlice.request` (through
   * `getOpenForms`) to decide between silently closing a clean form or
   * prompting the user.
   */
  formIsDirty: boolean;

  // --- Actions ---
  openDetails: () => void;
  closeDetails: () => void;
  openForm: (mode: 'create' | 'edit', itemId?: string) => void;
  closeForm: () => void;
  setFormAnchorPoint: (point: Coordinates | null) => void;
  /** Update the dirty flag from the active form component. */
  setFormIsDirty: (value: boolean) => void;
  /**
   * Select an item by id. Thin alias over the slice-specific selector
   * (`selectTask` / `selectNote` / …) used by `useSyncOverlayWithUrl` to
   * open an overlay generically from the artifact registry.
   */
  selectItem: (id: string) => void;
}

export interface ArtifactDefinition {
  /** Stable id = plural URL segment (`/map/<id>`). */
  id: string;
  /** Singular URL segment (`/map/<singular>/:itemId`). */
  singular: string;
  /** Tabler icon component. */
  icon: ComponentType<{ size?: number }>;
  /** i18n label key for the ArtifactBar tab. */
  labelKey: string;
  /** View modes this artifact supports (order = tab order in ViewToggle). */
  supportedViews: readonly ViewMode[];
  /** Default view when `?view=` is absent or invalid. */
  defaultView: ViewMode;
  /** Background page component rendered under `/map/<id>`. */
  BackgroundPage: ComponentType;
  /** Component rendered inside the SidePanel body when this artifact is active. */
  PanelContent: ComponentType<{ onClose: () => void }>;
  /**
   * Detail content rendered inside the unified SlidePanel overlay
   * (`UnifiedDetailOverlay`). Receives no props — reads its own store
   * slice and publishes header/toolbar slots to the shell via
   * `useRegisterDetailSlots`.
   */
  DetailContent: ComponentType;
  /** Form panel rendered for this artifact's overlay (visibility driven by slice flags). */
  FormPanel: ComponentType;
  /** Live accessor for the artifact's store slice. */
  getSlice: (state: GeoMarkupStore) => ArtifactSlice;
  /** Store selectors used by the ArtifactBar for visual state. */
  selectors: {
    hasActiveFilters: (state: GeoMarkupStore) => boolean;
    isLayerVisible: (state: GeoMarkupStore) => boolean;
  };
  /**
   * Side-effect fired when URL `?view=` changes — typically mirrors the
   * view mode into the persistence store so `settingsBridge` can save it
   * as the user's default view. Optional: artifacts that do not need
   * persistence can omit it.
   */
  syncViewMode?: (view: ViewMode, store: GeoMarkupStore) => void;
}

// ========== Registry ==========

// Imports placed after the contract declarations so downstream files can
// safely `import type { ArtifactDefinition } from './registry'` without
// pulling the runtime artifact modules into the type-resolution graph.
import { isArtifactEnabled } from '@/config/features';
import { tasksArtifact } from './tasks';
import { notesArtifact } from './notes';
import { obstructionsArtifact } from './obstructions';

export const ARTIFACTS = {
  tasks: tasksArtifact,
  notes: notesArtifact,
  obstructions: obstructionsArtifact,
} as const;

export type ArtifactId = keyof typeof ARTIFACTS;

// ========== Helpers ==========

/**
 * Resolve an artifact definition by id. Returns `null` when the id is
 * unknown so callers can fall through gracefully (used by URL routing
 * and the form-conflict slice).
 */
export function getArtifact(id: string): ArtifactDefinition | null {
  if (!isArtifactEnabled(id)) return null;
  const entry = (ARTIFACTS as Record<string, ArtifactDefinition | undefined>)[
    id
  ];
  return entry ?? null;
}

const ALL_ARTIFACTS: readonly ArtifactDefinition[] = Object.values(
  ARTIFACTS
).filter((artifact) => isArtifactEnabled(artifact.id));

export function getAllArtifacts(): readonly ArtifactDefinition[] {
  return ALL_ARTIFACTS;
}

export function findArtifactBySingular(
  singular: string
): ArtifactDefinition | null {
  for (const artifact of ALL_ARTIFACTS) {
    if (artifact.singular === singular) return artifact;
  }
  return null;
}
